/**
 * Tracks pending invitation ids that triggered the current onboarding
 * session. Supports both invite kinds:
 *
 * - workspace invitations (`/join?id=...`)
 * - board invitations (`/join/board?id=...`)
 *
 * The stash is read by onboarding branch logic to route users through the
 * unified `/onboard/invitations` chooser before workspace creation.
 *
 * `sessionStorage` (not localStorage) so the id is scoped to the current
 * browser tab/session and disappears on tab close without any cleanup.
 */
const KEY = "onboarding:pendingInvites";
const LEGACY_KEY = "onboarding:pendingJoinId";

type PendingInvites = {
  orgInviteId: string | null;
  boardInviteId: string | null;
};

const EMPTY_PENDING_INVITES: PendingInvites = {
  orgInviteId: null,
  boardInviteId: null,
};

const isBrowser = () =>
  typeof window !== "undefined" && !!window.sessionStorage;

const writePendingInvites = (value: PendingInvites): void => {
  if (!isBrowser()) return;
  if (!value.orgInviteId && !value.boardInviteId) {
    window.sessionStorage.removeItem(KEY);
    return;
  }
  window.sessionStorage.setItem(KEY, JSON.stringify(value));
};

const normalizePendingInvites = (value: unknown): PendingInvites => {
  if (typeof value !== "object" || value === null) {
    return { ...EMPTY_PENDING_INVITES };
  }
  const orgInviteId =
    typeof (value as { orgInviteId?: unknown }).orgInviteId === "string"
      ? (value as { orgInviteId: string }).orgInviteId
      : null;
  const boardInviteId =
    typeof (value as { boardInviteId?: unknown }).boardInviteId === "string"
      ? (value as { boardInviteId: string }).boardInviteId
      : null;
  return { orgInviteId, boardInviteId };
};

export const getPendingInvites = (): PendingInvites => {
  if (!isBrowser()) return EMPTY_PENDING_INVITES;

  const raw = window.sessionStorage.getItem(KEY);
  if (raw) {
    try {
      return normalizePendingInvites(JSON.parse(raw));
    } catch {
      // Fall through to legacy key migration.
    }
  }

  // Backward-compat migration: previous code stored a plain org invite id.
  const legacyOrgInviteId = window.sessionStorage.getItem(LEGACY_KEY);
  if (legacyOrgInviteId) {
    const migrated: PendingInvites = {
      orgInviteId: legacyOrgInviteId,
      boardInviteId: null,
    };
    writePendingInvites(migrated);
    window.sessionStorage.removeItem(LEGACY_KEY);
    return migrated;
  }

  return { ...EMPTY_PENDING_INVITES };
};

export const setPendingOrgInviteId = (id: string): void => {
  const next = { ...getPendingInvites(), orgInviteId: id };
  writePendingInvites(next);
};

export const setPendingBoardInviteId = (id: string): void => {
  const next = { ...getPendingInvites(), boardInviteId: id };
  writePendingInvites(next);
};

export const getPendingOrgInviteId = (): string | null =>
  getPendingInvites().orgInviteId;

export const getPendingBoardInviteId = (): string | null =>
  getPendingInvites().boardInviteId;

export const clearPendingOrgInviteId = (): void => {
  const next = { ...getPendingInvites(), orgInviteId: null };
  writePendingInvites(next);
};

export const clearPendingBoardInviteId = (): void => {
  const next = { ...getPendingInvites(), boardInviteId: null };
  writePendingInvites(next);
};

export const clearPendingInvites = (): void => {
  writePendingInvites(EMPTY_PENDING_INVITES);
};

export const hasPendingInviteStash = (): boolean => {
  const pending = getPendingInvites();
  return !!pending.orgInviteId || !!pending.boardInviteId;
};

// Backward-compatible org-invite helpers used across existing routes.
export const setPendingJoinId = (id: string): void => {
  setPendingOrgInviteId(id);
};

export const getPendingJoinId = (): string | null => getPendingOrgInviteId();

export const clearPendingJoinId = (): void => {
  clearPendingOrgInviteId();
};
